import { useEffect, useRef, useState } from "react";
import { ServiceHistorySkeletion } from "../../../components/elements/skeletions/skeletions";
import { BookingInitButton } from "./BookingInitButton";
import { useServiceCarStore } from "../../../store/serviceCarStore";
import { useGetInitiateService, useGetServiceListing } from "../../../hooks/react-query/useNewService";
import { InfoIcon, TrashIcon } from "../../../icons";
import { getServiceBookingCancelFn } from "../../../transactions/serviceBooking/newServiceBooking";
import { validateResponse } from "../serviceUtils";
import { toast } from "sonner";
import { queryClient } from "../../../utils/queryClient";
import { QUERY_KEYS } from "../../../config/constants";
import { ConfirmationDialog, CustomModal } from "../../../components/ui";
import { TooltipComponent } from "../../../components/ui/Tooltip";

export const ServiceHistory = ({ state, dispatch }) => {
  const [cancelTargetId, setCancelTargetId] = useState(null);
  const [isCancelLoading, setIsCancelLoading] = useState(false)
  const {
    SERVICE_HISTORY
  } = QUERY_KEYS;

  const { carInfo } = useServiceCarStore();
  // const { isLoyaltyEligible, isLoyaltyEnrolled } = usePersonStore.getState();
  const plateRef = useRef(carInfo?.plateno);

  const { data, isLoading } = useGetServiceListing(plateRef.current)
  const { data:createBookingData, isLoading: createBookingLoading } = useGetInitiateService(plateRef.current)
  // const { isLoading: isProductsLoading} = useLoyaltyProducts({enabled: !!isLoyaltyEligible})

  const statusOrder = {
    "in progress": 1,
    "pending customer confirmation": 2,
    "scheduled": 3,
    "completed": 4,
    "canceled": 5,
  };
  
  const sortByStatusAndDate = (a, b) => {
    const statusA = statusOrder[a?.status?.toLowerCase()] ?? 999;
    const statusB = statusOrder[b?.status?.toLowerCase()] ?? 999;

    // First sort by status priority
    if (statusA !== statusB) {
      return statusA - statusB;
    }

    // Then sort by date descending within same status
    return (
      new Date(b.bookingDate ?? 0) -
      new Date(a.bookingDate ?? 0)
    );
  };

  const tableData = [...(data ?? [])].sort(sortByStatusAndDate);
  
  useEffect(() => {
    if(createBookingData) {
      dispatch({type: "SET_SHOW_LIGHT_INDICATOR", showServiceLightIndicatorNeeded:Object.keys(createBookingData?.[0]?.inputs)?.includes("serviceLightIndicator")})
    }
  },[createBookingData])



  const getServiceTitle = (serviceType) => {
    const value = serviceType?.trim();

    if (!value) return "Λοιπές Εργασίες";

    if (value.includes("Γενικό Service")) {
      return "Γενικό Service";
    }

    if (["Failure", "Van Failure", "Emergency"].includes(value)) {
      return "Μηχανική Βλάβη";
    }

    if (value.includes("Ενδιάμεσο Service")) {
      return "Ενδιάμεσο Service";
    }

    if (["Γενικός Έλεγχος", "Van Γενικός Έλεγχος"].includes(value)) {
      return "Γενικός Έλεγχος";
    }

    if (["Fast Track", "Van Fast Track"].includes(value)) {
      return "Λοιπές Εργασίες";
    }

    return "Λοιπές Εργασίες";
  };

  
  const title = (info) => {
    const hasService = !!info?.hasBookedService;
    const hasTyres = !!info?.hasBookedTyresChange;
    const hasBodyWork = !!info?.hasBookedBodyWork;

    if (hasService && !hasTyres && !hasBodyWork) {
      return getServiceTitle(info?.serviceType);
    }

    if (!hasService && hasTyres && !hasBodyWork) {
      return "Ελαστικά";
    }

    if (!hasService && !hasTyres && hasBodyWork) {
      return "Φανοποιία";
    }

    if (!hasService && !hasTyres && !hasBodyWork) {
      return "Λοιπές Εργασίες";
    }

    return `${hasService ? "Service" : ""} ${hasTyres ? "Ελαστικά" : ""} ${hasBodyWork ? "Φανοποιία" : ""}`.trim();
  };

  // const title = (info) => {
  //   if(info?.serviceType !== null) {
  //     return `${info?.hasBookedService ? info.serviceType : ""}  ${info?.hasBookedTyresChange ? "Ελαστικά" : ""} ${info?.hasBookedBodyWork ? "Φανοποιία" : ""}`
  //   } else {
  //     return `${info?.hasBookedService ? "Service" : ""} ${info?.hasBookedTyresChange ? "Ελαστικά" : ""} ${info?.hasBookedBodyWork ? "Φανοποιία" : ""}`
  //   }
  // }

  const status = (status) => {
      switch (status.toLowerCase()) {
        case "scheduled":
          return <div className="flex flex-row gap-2 items-center justify-center">
            <div className='h-2 w-2 rounded-full bg-success'/>
            <p>Προγραμματισμένο</p>
          </div>;
        case "completed":
          return <div className="flex flex-row gap-2 items-center justify-center">
            <div className='h-2 w-2 rounded-full bg-fadedgrey'/>
            <p>Ολοκληρωμένο</p>
          </div>;
        case "canceled":
          return <div className="flex flex-row gap-2 items-center justify-center">
            <div className='h-2 w-2 rounded-full bg-primary'/>
            <p>Ακυρώθηκε</p>
          </div>;
        case "pending customer confirmation":
          return <div className="flex flex-row gap-2 items-center justify-center">
            <div className='h-2 w-2 rounded-full bg-[#177EFF]'/>
            <p>Προς Έγκριση</p>
          </div>;
        case "in progress":
          return <div className="flex flex-row gap-2 items-center justify-center">
            <div className='h-2 w-2 rounded-full bg-[#FF6F00]'/>
            <p>Σε Εξέλιξη</p>
          </div>;
        default:
          return '-';
      }
    };

  // USE EFFECTS:

  // HANDLERS & FUNCTIONS
  const handleCancelAppointment = async (serviceFlowId) => {
    try {
      setIsCancelLoading(true)
      const response = await getServiceBookingCancelFn({serviceFlowId:serviceFlowId, payload:{}});
      if (validateResponse(response)) {
        queryClient.invalidateQueries({
          queryKey: [SERVICE_HISTORY],
        });
        toast({
          variant: 'success',
          description: 'Το ραντεβού ακυρώθηκε',
        });
      }
      setCancelTargetId(null);
    } catch (e) {
      console.warn('Undo failed', e);
    } finally {
      setIsCancelLoading(false)
    }
  }
  
  const initBookingFlow = () => {
    dispatch({ type: "SET_STEP", step: state.step + 1 });
  };

  return (
    <>
      <div className="bg-white w-full rounded-[12px] flex flex-col gap-6 p-6 h-fit">
        <div className="flex justify-between items-center">
          <h4>Ιστορικό Service</h4>
          {/* <BookingInitButton initBookingFlow={initBookingFlow} disabled={createBookingLoading || ((isLoyaltyEligible && isLoyaltyEnrolled && isProductsLoading))} /> */}
          <BookingInitButton initBookingFlow={initBookingFlow} disabled={createBookingLoading} />
        </div>
        {isLoading ? <ServiceHistorySkeletion /> :
          tableData?.length > 0 ? <div className="relative">
            <div className="max-h-[720px] overflow-y-auto pr-3">
              <table className="w-full border-separate border-spacing-0">
                <thead className="sticky top-0 z-90 bg-white">
                  <tr className="">
                    <th className="text-left text-midgrey py-3 text-sm font-semibold ">Τύπος εργασίας</th>
                    <th className="text-center text-midgrey py-3 text-sm font-semibold">Χλμ. εισαγωγής</th>
                    <th className="text-center text-midgrey py-3 text-sm font-semibold">Status</th>
                    <th className="text-center text-midgrey py-3 text-sm font-semibold">Ημέρα Εισαγωγής</th>
                    <th className="text-right text-midgrey py-3 text-sm font-semibold">Ημέρα Εξαγωγής</th>
                  </tr>
                  <tr>
                    <th colSpan={5} className="pb-4">
                      <div className='border-b border-fadedgrey'/>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map(item => (
                    <tr
                      key={item.serviceAppointmentId}
                      className="border-b border-mildgrey last:border-b-0"
                    >
                      <td className={`${['in progress','pending customer confirmation'].includes(item?.status.toLowerCase()) && 'font-semibold'} text-left text-midgrey py-3 text-sm`}>{title(item)}</td>
                      <td className={`${['in progress','pending customer confirmation'].includes(item?.status.toLowerCase()) && 'font-semibold'} text-center text-midgrey py-3 text-sm`}>{Math.trunc(item.mileage)} km</td>
                      <td className={`${['in progress','pending customer confirmation'].includes(item?.status.toLowerCase()) && 'font-semibold'} text-center text-midgrey py-3 text-sm`}>{status(item?.status)}</td>
                      <td className={`${['in progress','pending customer confirmation'].includes(item?.status.toLowerCase()) && 'font-semibold'} text-center text-midgrey py-3 text-sm`}>
                        {item.bookingDate
                          ? new Date(item.bookingDate).toLocaleDateString('el-GR', {
                              year: 'numeric',
                              month: 'long',
                              day: '2-digit',
                            })
                          : 'Εκκρεμεί'}
                      </td>
                      <td className={`${['in progress','pending customer confirmation'].includes(item?.status.toLowerCase()) && 'font-semibold'} py-3 text-sm text-midgrey flex flex-row justify-end items-center gap-1`}>
                        {item.actualEndDate
                          ? new Date(item.actualEndDate).toLocaleDateString('el-GR', {
                              year: 'numeric',
                              month: 'long',
                              day: '2-digit',
                            })
                          : '-'}
                          {
                            (item.isCancellable && !!item?.serviceFlow?.id)
                              ? (
                                <CustomModal
                                  triggerComponent={<TrashIcon color="#D4002A" width="24" height="24" />}
                                  openModal={cancelTargetId === item.serviceAppointmentId}
                                  handleClick={(value) => {
                                    if (value) setCancelTargetId(item.serviceAppointmentId);
                                    else setCancelTargetId(null);
                                  }}
                                >
                                  <div className="container-sm mx-auto py-10">
                                    <ConfirmationDialog
                                      message={
                                        <p>
                                          Θέλεις να ακυρώσεις το ραντεβού "{title(item)?.trimStart()}"{" "}
                                          {item?.bookingDate
                                            ? `την ${new Date(item.bookingDate).toLocaleDateString("el-GR", {
                                                weekday: "long",
                                                timeZone: "Europe/Athens",
                                              })} ${new Date(item.bookingDate).toLocaleDateString("el-GR", {
                                                year: "numeric",
                                                month: "long",
                                                day: "2-digit",
                                              })}`
                                            : ""}{" "}
                                          για το {carInfo?.plateno};
                                        </p>
                                      }
                                      title="Ακύρωση προγραμματισμένου ραντεβού"
                                      confirmCtaText="Ακύρωση"
                                      cancelCtaText="Επιστροφή"
                                      isLoading={isCancelLoading}
                                      handleOkClick={() => handleCancelAppointment(item?.serviceFlow?.id)}
                                      handleCancelClick={() => setCancelTargetId(null)}
                                    />
                                  </div>
                                </CustomModal>
                              )
                            : new Date(item?.bookingDate).getTime() > Date.now()
                              ? (<TooltipComponent
                                  side="top"
                                  content={
                                    <div className="w-52 lg:w-72">
                                      <p>
                                        Για ακύρωση του ραντεβού, επικοινώνησε στο (+30)2106879800 ή στο
                                        contact@avis.gr
                                      </p>
                                    </div>
                                  }
                                >
                                  <div>
                                    <InfoIcon className="w-6 h-6" color="#2E9BFF" noStroke={true} />
                                  </div>
                                </TooltipComponent>
                              )
                            :null
                          }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div> 
          : <div>
            Δεν υπάρχει ιστορικό
          </div>
          }
      </div>
    </>
  );
};
